import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  Login,
  AuthActionTypes,
  Logout,
  SignUp,
  ResetPassword,
  LoginWithGoogle,
  ApplyAuthCode,
  LoginSuccessful,
  LoginFailed,
  SendPasswordReset,
  AuthEffectsInit
} from '../actions/auth.actions';
import { filter, map, switchMap, first, catchError, tap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { MarkAsSubmittedAction, MarkAsUnsubmittedAction } from 'ngrx-forms';
import { AUTH_LOGIN_FORM_ID as LOGIN_FORM_ID } from '../reducers/login.reducer';
import { getLoginForm } from '../selectors/login.selectors';
import {
  of,
  defer
} from 'rxjs';
import { AUTH_SIGN_UP_FORM_ID } from '../reducers/sign-up.reducer';
import { getSignUpForm } from '../selectors/sign-up.selectors';
import { DigiAIXCoreAppState } from '../../app-state';
import {
  ShowInfoMessage,
  ShowErrorMessage
} from '../../notification/notification.actions';
import { AuthService, AuthProviders } from '../auth.service';
import { NGXLogger } from 'ngx-logger';
import { isAuthEnabled } from '../../config/config.selectors';
import { AppNavigateTo } from '../../life-cycle/life-cycle.actions';

@Injectable()
export class AuthEffects<AppState extends DigiAIXCoreAppState> {

  @Effect()
  public init$ = defer(() => of(new AuthEffectsInit()).pipe(
    tap(() => this.logger.info('[AuthEffects::init$]')),
  ));

  @Effect({ resubscribeOnError: true })
  public login$ = this.actions$.pipe(
    ofType<Login>(AuthActionTypes.Login),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(action =>
      this.auth.signInWithEmailAndPassword(action.email, action.password).pipe(
        switchMap(() => [
          new ShowInfoMessage('DX.AUTH.LOGIN.SUCCESSFUL'),
          new MarkAsUnsubmittedAction(LOGIN_FORM_ID),
          new LoginSuccessful()
        ]),
        catchError(err =>
          of(
            new ShowErrorMessage('DX.AUTH.LOGIN.FAILED', err),
            new MarkAsUnsubmittedAction(LOGIN_FORM_ID),
            new LoginFailed()
          )
        )
      )
    )
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public loginWithGoogle$ = this.actions$.pipe(
    ofType<LoginWithGoogle>(AuthActionTypes.LoginWithGoogle),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(() => this.auth.signInWithProvider(AuthProviders.Google))
  );

  @Effect({ resubscribeOnError: true })
  public logout$ = this.actions$.pipe(
    ofType<Logout>(AuthActionTypes.Logout),
    switchMap(() =>
      this.auth.signOut().pipe(
        map(() => new ShowInfoMessage('DX.AUTH.LOGOUT.SUCCESSFUL')),
        catchError(err =>
          of(new ShowErrorMessage('DX.AUTH.LOGOUT.FAILED', err))
        )
      )
    )
  );

  @Effect({ dispatch: false, resubscribeOnError: true })
  public logoutAndReset$ = this.actions$.pipe(
    ofType<Logout>(AuthActionTypes.Logout),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    tap(() => this.auth.reset())
  );

  @Effect({ resubscribeOnError: true })
  public loginSubmit$ = this.actions$.pipe(
    ofType<MarkAsSubmittedAction>(MarkAsSubmittedAction.TYPE),
    filter(action => action.controlId === LOGIN_FORM_ID),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(() =>
      this.store$.pipe(
        select(getLoginForm),
        first(),
        filter(formState => formState.isValid),
        map(formState => formState.value)
      )
    ),
    map(value => new Login(value.email, value.password))
  );

  @Effect({ resubscribeOnError: true })
  public signUpSubmit$ = this.actions$.pipe(
    ofType<MarkAsSubmittedAction>(MarkAsSubmittedAction.TYPE),
    filter(action => action.controlId === AUTH_SIGN_UP_FORM_ID),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(() =>
      this.store$.pipe(
        select(getSignUpForm),
        first(),
        filter(formState => formState.isValid),
        map(formState => formState.value)
      )
    ),
    switchMap(value => [
      new SignUp(value.email, value.password),
      new MarkAsUnsubmittedAction(AUTH_SIGN_UP_FORM_ID)
    ])
  );

  @Effect({ resubscribeOnError: true })
  public signUp = this.actions$.pipe(
    ofType<SignUp>(AuthActionTypes.SignUp),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(action =>
      this.auth
        .createUserWithEmailAndPassword(action.email, action.password)
        .pipe(
          map(() => new ShowInfoMessage('DX.AUTH.SIGN_UP.SUCCESSFUL')),
          catchError(err =>
            of(new ShowErrorMessage('DX.AUTH.SIGN_UP.FAILED', err))
          )
        )
    )
  );

  @Effect()
  public sendPasswordReset$ = this.actions$.pipe(
    ofType<SendPasswordReset>(SendPasswordReset.TYPE),
    switchMap(action =>
      this.auth.sendPasswordReset(action.password, action.token).pipe(
        switchMap(() => [
          new AppNavigateTo(
            ['/', 'auth', 'login'],
            'AuthEffects::sendPasswordReset$'
          ),
          new ShowInfoMessage('DX.AUTH.PASSWORD_RESET.SUCCESSFUL')
        ]),
        catchError(err =>
          of(new ShowErrorMessage('DX.AUTH.PASSWORD_RESET.FAILED', err))
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public resetPassword$ = this.actions$.pipe(
    ofType<ResetPassword>(AuthActionTypes.ResetPassword),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(action =>
      this.auth.sendPasswordResetEmail(action.email).pipe(
        tap(result =>
          this.logger.debug('[AuthEffects::resetPassword$] result', result)
        ),
        map(
          () => new ShowInfoMessage('DX.AUTH.PASSWORD_RESET_REQUEST.SUCCESSFUL')
        ),
        catchError(err =>
          of(new ShowErrorMessage('DX.AUTH.PASSWORD_RESET_REQUEST.FAILED', err))
        )
      )
    )
  );

  @Effect({ resubscribeOnError: true })
  public applyAuthCode$ = this.actions$.pipe(
    ofType<ApplyAuthCode>(AuthActionTypes.ApplyAuthCode),
    switchMap(action =>
      this.store$.pipe(
        select(isAuthEnabled),
        filter(Boolean),
        map(() => action)
      )
    ),
    switchMap(action => this.auth.applyActionCode(action.oobCode))
  );

  constructor(
    private actions$: Actions,
    private auth: AuthService,
    private store$: Store<AppState>,
    private logger: NGXLogger
  ) {}
}
